import BaseController from "confinus_ui/base_controller";
import { showConfirmModal, ModalLifecycleEvent } from "lib/overlay/modal";

// Intercepts clicks on actions declared with confirm: { ... }, opens a
// ConfinusUI::Modal::Confirm dialog, and on confirmation re-dispatches a
// click to the underlying element so its native behaviour (link follow,
// form submit, modal open) takes effect.
//
// The intercept is one-shot: a `data-page-actions-confirmed` attribute is
// set on the element after confirmation so the second synthetic click
// passes through without re-triggering the guard.
export default class extends BaseController {
  static values = {
    title: String,
    body: String,
    confirmLabel: String,
    intent: { type: String, default: "neutral" },
  };

  declare titleValue: string;
  declare bodyValue: string;
  declare confirmLabelValue: string;
  declare intentValue: string;

  guard(event: Event) {
    // Re-entrant click after confirmation — let it through.
    if (this.element.hasAttribute("data-page-actions-confirmed")) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    showConfirmModal({
      title: this.titleValue,
      message: this.bodyValue,
      type: this.intentValue === "destructive" ? "destructive" : "info",
      positiveButtonLabel: this.confirmLabelValue,
      positiveButtonReason: "confirm",
      negativeButtonLabel: "Cancel",
      negativeButtonReason: "cancel",
      onClosing: this.handleDialogClosing,
    });
  }

  private handleDialogClosing = ({
    detail: { closeReason },
  }: ModalLifecycleEvent) => {
    if (closeReason !== "confirm") return;

    // Primary actions wrap the real control in a <div data-action-id> that
    // carries this controller, so a synthetic click on `this.element` (the
    // wrapper) never reaches the inner <form>/<a>. Submit the inner form
    // directly, or click the inner link, when present. Menu items ARE the
    // <a>/<button> themselves (no inner control), so they fall through to the
    // set → click → unset path that re-fires the element's own behaviour.
    const form = this.element.querySelector("form");
    if (form) {
      (form as HTMLFormElement).requestSubmit();
      return;
    }

    const inner = this.element.querySelector(
      "a[href], button",
    ) as HTMLElement | null;
    const target = inner ?? (this.element as HTMLElement);
    // Set → click → unset. The synthetic click runs synchronously and the
    // listener checks the flag during that call; clearing it afterwards
    // prevents the flag persisting past Turbo morphs that retain this node.
    this.element.setAttribute("data-page-actions-confirmed", "true");
    target.click();
    this.element.removeAttribute("data-page-actions-confirmed");
  };
}
